// services/motorPriorityPolicy.ts
import type { ActionableAdvice, DiagnosticExplanation, MotorPrescription } from "../types";

const DOMAIN_PRIORITY: Record<string, number> = {
  TIMING: 10,
  STABILITY: 20,
  ATTAQUE: 30,
  HARMONY: 40
};

export function buildMotorPrescription(
  adv: ActionableAdvice,
  explanation: DiagnosticExplanation,
  lang: "fr" | "en"
): MotorPrescription | null {
  const isFr = lang === "fr";

  // Jamais de prescription sur SUCCESS
  if (adv.severity === "SUCCESS") return null;

  // Force mode uniquement si CRITICAL
  const force = adv.severity === "CRITICAL";

  if (adv.domain === "TIMING") {
    const rushing = adv.icon === "🐇";
    const dragging = adv.icon === "🐢";

    if (rushing) {
      return {
        motor_action_id: "TIMING_CALM_SWING_30S",
        domain: adv.domain,
        priority: DOMAIN_PRIORITY.TIMING,
        title: isFr ? "Stabiliser le balancier" : "Stabilize the swing",
        instruction: isFr
          ? "STOP. 30 secondes : main droite en balancier lourd, respiration lente."
          : "STOP. 30s: heavy pendulum right-hand, slow breathing.",
        steps: isFr
          ? ["Compte 1-2-3-4 à voix basse.", "Balancier ample (sans accélérer).", "Rejoue tout de suite."]
          : ["Count 1-2-3-4 quietly.", "Wide pendulum (no speeding).", "Replay immediately."],
        duration_s: 30,
        force_mode: force,
        recheck_signal_id: explanation.signal_id
      };
    }

    if (dragging) {
      return {
        motor_action_id: "TIMING_ANTICIPATE_IMPACT_30S",
        domain: adv.domain,
        priority: DOMAIN_PRIORITY.TIMING,
        title: isFr ? "Anticiper l’impact" : "Anticipate impact",
        instruction: isFr
          ? "STOP. 30 secondes : anticipe chaque attaque, comme si tu “posais” le temps."
          : "STOP. 30s: anticipate each attack, as if you place the beat.",
        steps: isFr
          ? ["Métronome mental : 1-2-3-4.", "Attaque légèrement AVANT le clic.", "Rejoue tout de suite."]
          : ["Mental metronome: 1-2-3-4.", "Attack slightly BEFORE the click.", "Replay immediately."],
        duration_s: 30,
        force_mode: force,
        recheck_signal_id: explanation.signal_id
      };
    }

    // fallback timing (si icon non standard)
    return {
      motor_action_id: "TIMING_ANCHOR_30S",
      domain: adv.domain,
      priority: DOMAIN_PRIORITY.TIMING,
      title: isFr ? "Ancrage tempo" : "Tempo anchor",
      instruction: isFr
        ? "30 secondes : balancier constant, sans micro-corrections."
        : "30s: constant swing, no micro-corrections.",
      steps: isFr ? ["Balancier simple.", "Aucune accélération.", "Rejoue."] : ["Simple swing.", "No acceleration.", "Replay."],
      duration_s: 30,
      force_mode: force,
      recheck_signal_id: explanation.signal_id
    };
  }

  if (adv.domain === "ATTAQUE") {
    return {
      motor_action_id: "ATTACK_PICK_TIP_30S",
      domain: adv.domain,
      priority: DOMAIN_PRIORITY.ATTAQUE,
      title: isFr ? "Redonner du front" : "Restore the transient",
      instruction: isFr
        ? "STOP. 30 secondes : attaque uniquement avec le bout du médiator, geste court."
        : "STOP. 30s: attack only with the pick tip, short motion.",
      steps: isFr
        ? ["Médiator visible : 2–3 mm.", "Geste court, pas de balayage.", "Rejoue immédiatement."]
        : ["Show 2–3mm of pick tip.", "Short stroke, no sweeping.", "Replay immediately."],
      duration_s: 30,
      force_mode: force,
      recheck_signal_id: explanation.signal_id
    };
  }

  if (adv.domain === "STABILITY") {
    return {
      motor_action_id: "STABILITY_ANCHOR_EYES_CLOSED_30S",
      domain: adv.domain,
      priority: DOMAIN_PRIORITY.STABILITY,
      title: isFr ? "Ancrage interne" : "Inner anchor",
      instruction: isFr
        ? "30 secondes : joue en gardant le tempo *dans le corps* (yeux fermés si possible)."
        : "30s: keep tempo in your body (eyes closed if possible).",
      steps: isFr
        ? ["Relâche épaules.", "Micro-balayage constant.", "Rejoue juste après."]
        : ["Relax shoulders.", "Constant micro-swing.", "Replay right after."],
      duration_s: 30,
      force_mode: force,
      recheck_signal_id: explanation.signal_id
    };
  }

  if (adv.domain === "HARMONY") {
    return {
      motor_action_id: "HARMONY_RINGING_CHECK_20S",
      domain: adv.domain,
      priority: DOMAIN_PRIORITY.HARMONY,
      title: isFr ? "Faire chanter l’accord" : "Make the chord ring",
      instruction: isFr
        ? "20 secondes : fais sonner chaque corde une par une, puis rejoue l’accord complet."
        : "20s: ring each string one-by-one, then replay the full chord.",
      steps: isFr
        ? ["Joue corde par corde.", "Corrige l’étouffement.", "Rejoue l’accord."]
        : ["Play string-by-string.", "Fix muting.", "Replay the chord."],
      duration_s: 20,
      force_mode: force,
      recheck_signal_id: explanation.signal_id
    };
  }

  return null;
}
